import { useCallback, useEffect, useState } from 'react';
import {
  Clock, Megaphone, MessageCircle, Inbox, UploadCloud, Sparkles, AlertCircle, type LucideIcon,
} from 'lucide-react';
import { api } from '../../lib/api';
import type { ActivityEntry } from '../../lib/types';
import { LoadingState, EmptyState, ErrorState } from '../../components/ui/States';

const INTENT_ICON: Record<string, LucideIcon> = {
  'change-business-hours': Clock,
  'update-whatsapp-message': MessageCircle,
  'publish-announcement': Megaphone,
  'mark-leads-read': Inbox,
  'publish-site': UploadCloud,
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return 'Justo ahora';
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'Ayer' : `Hace ${days} días`;
}

/**
 * Feed de acciones que el asistente ejecutó (Fase 2). Se refresca al recibir
 * el evento 'activity:changed' que dispara el composer tras cada ejecución.
 */
export default function ActivityFeed() {
  const [entries, setEntries] = useState<ActivityEntry[] | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setError(false);
    api.assistant
      .activity(8)
      .then(setEntries)
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    load();
    const onChanged = () => load();
    window.addEventListener('activity:changed', onChanged);
    return () => window.removeEventListener('activity:changed', onChanged);
  }, [load]);

  if (error) {
    return (
      <div className="bg-ui-surface border border-ui-border rounded-xl">
        <ErrorState onRetry={load} />
      </div>
    );
  }

  if (entries === null) {
    return (
      <div className="bg-ui-surface border border-ui-border rounded-xl">
        <LoadingState />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="bg-ui-surface border border-ui-border rounded-xl">
        <EmptyState
          icon={Sparkles}
          title="Aún no hay acciones del asistente"
          description="Cuando le pidas algo al asistente, lo que haga por ti aparecerá aquí."
        />
      </div>
    );
  }

  return (
    <div className="bg-ui-surface border border-ui-border rounded-xl divide-y divide-ui-border">
      {entries.map((entry) => {
        const Icon = (entry.intentId && INTENT_ICON[entry.intentId]) || Sparkles;
        const failed = entry.status === 'error';
        return (
          <div key={entry.id} className="flex items-start gap-3 px-4 py-3">
            <span
              className={`mt-0.5 shrink-0 w-7 h-7 flex items-center justify-center rounded-lg ${
                failed ? 'bg-ui-danger/10 text-ui-danger' : 'bg-ui-accent/10 text-ui-accent'
              }`}
            >
              {failed ? <AlertCircle size={15} /> : <Icon size={15} strokeWidth={1.85} />}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-ui-text">{entry.summary}</p>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-ui-text-subtle">
                <span>{relativeTime(entry.createdAt)}</span>
                {entry.needsPublish && (
                  <span className="inline-flex items-center gap-1 text-ui-warning">
                    · Falta publicar para que se vea
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
